// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Shared crop-override controls for the I-9 retry dialogs.
 *
 * By default no crop is sent, so the processing machine applies its host-configured
 * band. The user can opt into an explicit crop override (e.g. an older-format scan the
 * default band misses); only then are crop fractions forwarded onto the to-do queue.
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Checkbox, Input, Label } from '@/components/ui'

export interface CropValues {
  cropTopFraction: number
  cropBottomFraction: number
}

interface CropOverride {
  // null = no override (use the host's configured band)
  crop: CropValues | null
  isValid: boolean
  element: ReactNode
}

// `open` resets the controls to "no override" each time the hosting dialog reopens.
export function useCropOverride(open: boolean): CropOverride {
  const [override, setOverride] = useState(false)
  const [top, setTop] = useState('0')
  const [bottom, setBottom] = useState('1')

  useEffect(() => {
    if (open) {
      setOverride(false)
      setTop('0')
      setBottom('1')
    }
  }, [open])

  const topNum = Number(top)
  const bottomNum = Number(bottom)
  const bandValid =
    top.trim() !== '' &&
    bottom.trim() !== '' &&
    !isNaN(topNum) &&
    !isNaN(bottomNum) &&
    topNum >= 0 &&
    bottomNum <= 1 &&
    topNum < bottomNum
  // When not overriding, there's nothing to validate -- the host band is used.
  const isValid = !override || bandValid
  const crop = override ? { cropTopFraction: topNum, cropBottomFraction: bottomNum } : null

  const element = (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id="cropOverride"
          checked={override}
          onCheckedChange={(c) => setOverride(c === true)}
        />
        <Label htmlFor="cropOverride">Override crop band</Label>
      </div>

      {override && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cropTop">Crop top</Label>
            <Input
              id="cropTop"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={top}
              onChange={(e) => setTop(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Fraction from the top (0–1)</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cropBottom">Crop bottom</Label>
            <Input
              id="cropBottom"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={bottom}
              onChange={(e) => setBottom(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Fraction from the top (0–1)</p>
          </div>
        </div>
      )}

      {override && !bandValid && (
        <p className="text-xs text-red-600">
          Top must be ≥ 0, bottom ≤ 1, and top must be less than bottom.
        </p>
      )}
    </div>
  )

  return { crop, isValid, element }
}
